/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';
import {
    DisplayHtmlContent,
    SlideShowWithFetch,
    FlexCol,
    FlexRow,
    Heading,
    StarRating,
    MobileCoverageRating,
    useWindowSize,
} from 'sarsaparilla';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { FavoriteHeart } from 'shared-ui/favorites';

export default function Header({
    facility,
    rating,
    parentLocation = [],
    locationAsset = [],
}) {
    const { width } = useWindowSize();
    const isMobile = width <= 768;
    const location = [];
    const metadata = [];
    const parentLocationSelected = parentLocation?.find(
        (item) => item.entity_id === facility?.parent_asset_id
    );
    const locationSelected = locationAsset?.find(
        (item) => item.entity_id === facility?.id
    );

    if (!isNil(parentLocationSelected?.name)) {
        location.push(
            <span>
                <a href={getGatewayUrl(facility?.parent_asset_id)}>
                    {parentLocationSelected?.name}
                </a>
            </span>
        );
    }

    if (!isNil(locationSelected?.state_code) && !isNil(locationSelected.city)) {
        location.push(
            <span>
                Near {locationSelected.city}, {locationSelected.state_code}
            </span>
        );
    }

    if (!isNil(rating?.average_rating) && !isNil(rating?.number_of_ratings)) {
        metadata.push(
            <StarRating
                rating={rating?.average_rating}
                reviewCount={rating?.number_of_ratings}
            />
        );
    }

    if (rating?.mobile_coverage_rating) {
        metadata.push(<MobileCoverageRating rating={rating?.mobile_coverage_rating} />);
    }

    metadata.push(
        <FavoriteHeart
            entityId={facility?.asset_id}
            entityType={'activitypass'}
            showFavoriteText
            key="favorite-button"
        />
    );

    const hasLocation = !!location.length;
    const hasMetadata = !!metadata.length;

    return (
        <div className="ap-header">
            {isMobile && (
                <FlexRow>
                    <FlexCol lg={12}>
                        <SlideShowWithFetch assetId={facility?.asset_id} />
                    </FlexCol>
                </FlexRow>
            )}
            <FlexRow>
                <FlexCol lg={6} className="mb-3 mb-lg-0">
                    <Heading headingLevel={1} appearance="h4" className="ap-header-title">
                        {facility?.name}
                    </Heading>
                    {hasLocation && (
                        <ul className="ap-header-metadata-list">
                            {location.map((item, key) => {
                                return (
                                    <li
                                        className="ap-header-metadata-list-item"
                                        key={key}
                                    >
                                        {item}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {hasMetadata && (
                        <ul className="ap-header-metadata-list">
                            {metadata.map((item, key) => {
                                return (
                                    <li
                                        className="ap-header-metadata-list-item"
                                        key={key}
                                    >
                                        {item}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {!isNil(facility?.contents?.overview) && (
                        <div className="ap-header-description">
                            {facility?.contents?.overview.map((item) => {
                                const paragraphs = item?.paragraphs;
                                return paragraphs?.map((paragraph, index) => (
                                    <DisplayHtmlContent
                                        key={`overview-paragraph-${index}`}
                                        html={`<p>${paragraph}</p>`}
                                    />
                                ));
                            })}
                        </div>
                    )}
                </FlexCol>
                {!isMobile && (
                    <FlexCol lg={6}>
                        <SlideShowWithFetch assetId={facility?.asset_id} />
                    </FlexCol>
                )}
            </FlexRow>
        </div>
    );
}

Header.propTypes = {
    rating: PropTypes.object,
    facility: PropTypes.object,
    parentLocation: PropTypes.any,
};
